import {
  useState,
  useCallback,
  useMemo,
  useImperativeHandle,
  useAppDispatch,
  useAppSelector,
} from "@/utils/hooks/reactUseCustomHooks";
import { setClearTotalTime } from "@/utils/redux/recipes/recipeSlice";
import { Checkbox, Text } from "@dxp-feed/ui";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@dxp-feed/ui/Accordion";
import { forwardRef, useEffect } from "react";

import { useHits, useNumericMenu } from "@dxp-feed/algolia";
import {
  clearPageFilters,
  setFilters,
  setPageFilters,
} from "@/utils/redux/recipes/algoliaSlice";
import { RecipeType } from "@/utils/types/recipes";
import {
  GLOBAL_SEARCH_RESULTS_SECTIONS,
  TIME_RANGE as timeRanges,
} from "@/utils/constant";

const TotalTimeFilter = forwardRef(({ onFilterChange }: any, ref) => {
  const { aemFilterData }: any = useAppSelector(
    (state) => state.getAemDataSlice
  );
  const filterTitle = aemFilterData?.totalTime;
  const [selectedFilter, setSelectedFilter] = useState(null);
  const { filters, totalTimeFilter } = useAppSelector(
    (state) => state.algoliaDataSlice
  );
  const { items: hitsItems } = useHits() as { items: RecipeType[] };
  const dispatch = useAppDispatch();

  const dynamicTimeItems = useMemo(() => {
    if (!totalTimeFilter || totalTimeFilter.length === 0) return [];

    return timeRanges.filter(({ end }) =>
      totalTimeFilter.some(
        (item: any) => parseFloat(item.value) <= end && item.count > 0
      )
    );
  }, [totalTimeFilter]);

  const [filterTime, setFilterTime] = useState(dynamicTimeItems);

  const { items: totalTimeItems, refine } = useNumericMenu({
    attribute: "cooking.totalTime",
    items: [
      { label: "All" },
      { label: "15 minutes or less", end: 15 },
      { label: "30 minutes or less", end: 30 },
      { label: "45 minutes or less", end: 45 },
      { label: "1 hour or less", end: 60 },
    ],
  });

  useEffect(() => {
    setFilterTime(() =>
      dynamicTimeItems.map((item) => {
        const match = totalTimeItems.find(
          (t) => t.label === item.label && t.isRefined
        );
        return {
          ...item,
          isRefined: Boolean(match),
        };
      })
    );
  }, [dynamicTimeItems, totalTimeItems]);

  const index = 0;
  const onChange = useCallback(
    (event: any, item: any) => {
      const isChecked = event.target.checked;
      if (isChecked) {
        if (selectedFilter == null) {
          const filtersCount = Math.max(0, filters + 1);
          dispatch(setPageFilters(GLOBAL_SEARCH_RESULTS_SECTIONS.RECIPE));
          dispatch(setFilters(filtersCount));
          setSelectedFilter(item.value);
        }
        refine(item.value);
        setFilterTime((prev) => {
          const updated = prev.map((ft) =>
            ft.value === item.value
              ? { ...ft, isRefined: isChecked }
              : { ...ft, isRefined: false }
          );
          return updated;
        });
        dispatch(setClearTotalTime(true));
      } else {
        refine("%7B%7D");
        setSelectedFilter(null);
        const filtersCount = Math.max(0, filters - 1);
        dispatch(setPageFilters(GLOBAL_SEARCH_RESULTS_SECTIONS.RECIPE));
        dispatch(setFilters(filtersCount));
        dispatch(setClearTotalTime(false));
      }
    },
    [dispatch, filters, refine]
  );

  const clearAllTimeFilter = () => {
    refine("%7B%7D");
    setSelectedFilter(null);
    dispatch(clearPageFilters(GLOBAL_SEARCH_RESULTS_SECTIONS.RECIPE));
    dispatch(setClearTotalTime(false));
    setFilterTime((prev) => {
      const updated = prev.map((ft) => ({ ...ft, isRefined: false }));
      return updated;
    });
  };

  useImperativeHandle(ref, () => ({
    clearAllTimeFilter,
  }));

  const accordianData = useMemo(() => {
    return filterTime.map((item: any, idx: number) => (
      <Text
        key={`checkbox-${index}-${idx}`}
        as="div"
        className="flex items-center gap-2"
      >
        <Checkbox
          id={`checkbox-${index}-${idx}`}
          className="m-[15px] min-w-[18px] min-h-[18px]"
          checked={item.isRefined}
          onChange={(event) => onChange(event, item)}
        />
        <label
          htmlFor={`checkbox-${index}-${idx}`}
          className="text-[14px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          {item?.label}
        </label>
      </Text>
    ));
  }, [totalTimeItems, filterTime, onChange]);

  if (!hitsItems.length || !filterTime.length) return null;

  return (
    <Accordion
      className="flex flex-col gap-6 lg:gap-4 border-b border-neutral200"
      defaultActiveKey={`itemTotalTime-${index}`}
      multiplePanelOpen={true}
    >
      <AccordionItem
        id={`itemTotalTime-${index}`}
        key={`itemTotalTime-${index}`}
      >
        <AccordionHeader
          id={`itemTotalTime-${index}`}
          className="font-bold !px-0 pt-0 pb-2 text-[16px] lg:text-[14px] group [&_.demo-icon]:w-6 [&_.demo-icon]:h-6 [&_.demo-icon]:flex [&_.demo-icon]:items-center [&_.demo-icon]:justify-center"
        >
          {filterTitle}
        </AccordionHeader>
        <AccordionPanel
          id={`itemTotalTime-${index}`}
          className="[&>div]:pl-0 [&>div]:flex [&>div]:flex-col [&>div]:gap-2 [&>div]:py-0 [&>div]:pb-2 lg:[&>div]:py-2 [&>div]:border-t [&>div]:border-none lg:[&>div]:border-neutral200 [&>div]:overflow-x-hidden [&>div]:overflow-y-auto [&>div]:max-h-[280px] [&>div]:scroll-bar-grey"
        >
          {accordianData}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
});
export default TotalTimeFilter;
